import React from 'react';
import { createPortal } from 'react-dom';
import { Badge } from './ui/badge';
import { MERResult } from '../types';
import { formatElectiveRequirementLabel, formatGerElectiveRequirementLabel, isIncludeSubjectsRequirement } from '../lib/utils/gerElectiveLabels';

type MERDisplayResult = MERResult & {
  requirements?: { [subject: string]: string | number };
};

interface GerBadgeProps {
  ger?: MERDisplayResult;
  translate: any;
}

interface PerBadgeProps {
  per?: MERDisplayResult;
  translate: any;
}

const getDefaultCoreMinimum = (subject: string): string | number => {
  if (subject === 'Citizenship and Social Development') return 'Attained';
  if (subject === 'Mathematics') return 2;
  return 3;
};

const formatAnySubjectLabel = (count: number, includeCores = false): string => {
  if (includeCores) {
    if (count === 1) return 'Other subject';
    if (count === 2) return 'Other subjects';
    return `Other ${count} subjects`;
  }

  if (count === 1) return 'Any one subject';
  if (count === 2) return 'Any two subjects';
  return `Any ${count} subjects`;
};

const minimumLevelCellClassName = 'grid min-w-[4.5rem] grid-cols-[minmax(0,1fr)_1.25rem] items-center justify-end gap-3';

function useTouchTooltip() {
  const [open, setOpen] = React.useState(false);
  const [isMounted, setIsMounted] = React.useState(false);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties>({});
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const touchedRef = React.useRef(false);
  const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearTimers = React.useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    clearTimeout(closeTimerRef.current);
  }, []);

  const updatePanelPosition = React.useCallback(() => {
    if (!wrapperRef.current || typeof window === 'undefined') {
      return;
    }

    const rect = wrapperRef.current.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 0;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportPadding = 16;
    const gap = 8;
    const preferredWidth = Math.min(800, viewportWidth - viewportPadding * 2);
    const minLeft = viewportPadding + preferredWidth / 2;
    const maxLeft = viewportWidth - viewportPadding - preferredWidth / 2;
    const centeredLeft = rect.left + rect.width / 2;
    const availableBelow = viewportHeight - rect.bottom - viewportPadding - gap;
    const availableAbove = rect.top - viewportPadding - gap;
    const shouldOpenAbove = panelHeight > availableBelow && availableAbove > availableBelow;
    const unclampedTop = shouldOpenAbove
      ? rect.top - panelHeight - gap
      : rect.bottom + gap;
    const top = Math.max(
      viewportPadding,
      Math.min(unclampedTop, viewportHeight - viewportPadding - panelHeight)
    );

    setPanelStyle({
      position: 'fixed',
      top,
      left: Math.max(minLeft, Math.min(centeredLeft, maxLeft)),
      transform: 'translateX(-50%)',
      width: preferredWidth,
      maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
      maxHeight: `calc(100vh - ${viewportPadding * 2}px)`,
      overflowY: 'auto',
      zIndex: 1000,
    });
  }, []);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!wrapperRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    updatePanelPosition();

    const handleViewportChange = () => updatePanelPosition();
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, updatePanelPosition]);

  React.useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  const handleTouchEnd = React.useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    touchedRef.current = true;
    setOpen(prev => !prev);
    setTimeout(() => { touchedRef.current = false; }, 400);
  }, []);

  const handleMouseEnter = React.useCallback(() => {
    if (touchedRef.current) return;
    clearTimeout(closeTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setOpen(true), 300);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    if (touchedRef.current) return;
    clearTimeout(hoverTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  }, []);

  const handlePanelMouseEnter = React.useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  const handlePanelMouseLeave = React.useCallback(() => {
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  }, []);

  return {
    open,
    isMounted,
    wrapperRef,
    panelRef,
    panelStyle,
    handleTouchEnd,
    handleMouseEnter,
    handleMouseLeave,
    handlePanelMouseEnter,
    handlePanelMouseLeave,
  };
}

const RequirementsTable: React.FC<{
  title: string;
  result: MERDisplayResult;
}> = ({ title, result }) => {
  const coreSubjects = Object.entries(result.core_minima || {});
  const customElectiveRequirements = Array.isArray(result.elective_requirements) ? result.elective_requirements : [];
  const electiveAlternatives = Array.isArray(result.elective_alternatives) ? result.elective_alternatives : [];
  const hasElectiveAlternatives = electiveAlternatives.length > 0;
  const hasSpecialPerDisplay = customElectiveRequirements.length === 0 && !!(result.one_of_groups || result.one_of_electives);
  const showGenericElectivesRow =
    !!result.electives &&
    result.electives.required > 0 &&
    customElectiveRequirements.length === 0 &&
    !hasElectiveAlternatives &&
    !hasSpecialPerDisplay &&
    !result.specific_electives &&
    !result.additional_any_one_subject &&
    !result.additional_any_two_subjects;

  return (
    <div className="w-full min-w-0 max-w-[800px] overflow-hidden rounded-lg border border-slate-300 bg-white shadow-[0_14px_34px_rgba(15,23,42,0.18)]">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <span className={result.eligible ? 'text-green-600' : 'text-red-600'}>{result.eligible ? '✓' : '✗'}</span>
      </div>

      <div className="grid grid-cols-2 divide-x divide-gray-200">
        <div>
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-4 py-2">
            <span className="text-xs font-medium text-gray-700">Core Subjects</span>
            <span className="text-xs font-medium text-gray-700">Minimum Level</span>
          </div>
          {coreSubjects.map(([subject, met]) => {
            const minimumLevel = result.requirements?.[subject] ?? getDefaultCoreMinimum(subject);
            const displayMinimumLevel = minimumLevel === 'A' || minimumLevel === 'a' ? 'Attained' : minimumLevel;

            return (
              <div key={subject} className="flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50">
                <span className="text-sm text-gray-800">{subject}</span>
                <div className={minimumLevelCellClassName}>
                  <span className="text-right text-sm font-medium text-gray-900">{displayMinimumLevel}</span>
                  <span className={`inline-block w-5 text-center ${met ? 'text-green-600' : 'text-red-600'}`}>{met ? '✓' : '✗'}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-4 py-2">
            <span className="text-xs font-medium text-gray-700">Elective Subject(s)</span>
            <span className="text-xs font-medium text-gray-700">Minimum Level</span>
          </div>

          {hasElectiveAlternatives && electiveAlternatives.map((path, pathIndex) => (
            <React.Fragment key={`alternative-${pathIndex}`}>
              {pathIndex > 0 && (
                <div className="border-b border-gray-200 bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-800">
                  Or
                </div>
              )}
              {path.map((requirement, requirementIndex) => (
                <div
                  key={`alternative-${pathIndex}-${requirementIndex}`}
                  className="flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50"
                >
                  <span className="text-sm text-gray-800">
                    {formatElectiveRequirementLabel(requirement)}
                  </span>
                  <div className={minimumLevelCellClassName}>
                    <span className="text-right text-sm font-medium text-gray-900">
                      {requirement.min_level ?? result.electives?.min_level ?? ''}
                    </span>
                    <span className={`inline-block w-5 text-center ${requirement.met ? 'text-green-600' : 'text-red-600'}`}>
                      {requirement.met ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              ))}
            </React.Fragment>
          ))}

          {/* Primary source: elective_requirements - non-include_subjects rows FIRST */}
          {!hasElectiveAlternatives && customElectiveRequirements.length > 0 && !hasSpecialPerDisplay && customElectiveRequirements
            .filter(r => !isIncludeSubjectsRequirement(r))
            .map((requirement, index) => (
            <div key={`er-any-${index}`} className="flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50">
              <span className="text-sm text-gray-800">{formatGerElectiveRequirementLabel(requirement)}</span>
              <div className={minimumLevelCellClassName}>
                <span className="text-right text-sm font-medium text-gray-900">
                  {requirement.min_level ?? result.electives?.min_level ?? ''}
                </span>
                <span className={`inline-block w-5 text-center ${requirement.met ? 'text-green-600' : 'text-red-600'}`}>
                  {requirement.met ? '✓' : '✗'}
                </span>
              </div>
            </div>
          ))}

          {/* Special: additional_any_one_subject ABOVE one_of sections */}
          {!hasElectiveAlternatives && result.additional_any_one_subject && customElectiveRequirements.length === 0 && (
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50">
              <span className="text-sm text-gray-800">Any one subject</span>
              <div className={minimumLevelCellClassName}>
                <span className="text-right text-sm font-medium text-gray-900">{result.additional_any_one_subject.min_level}</span>
                <span className={`inline-block w-5 text-center ${result.additional_any_one_subject.met ? 'text-green-600' : 'text-red-600'}`}>
                  {result.additional_any_one_subject.met ? '✓' : '✗'}
                </span>
              </div>
            </div>
          )}

          {/* Special programme display: one_of_groups (e.g. JS1202) */}
          {!hasElectiveAlternatives && customElectiveRequirements.length === 0 && result.one_of_groups?.map((group, idx) => (
            <React.Fragment key={idx}>
              <div className="border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800">
                {group.name || 'One of the following elective subjects'}
              </div>
              {group.subjects.map(subject => {
                const isMatched = group.matched_subjects?.includes(subject);
                return (
                  <div key={`${idx}-${subject}`} className="flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50">
                    <span className="text-sm text-gray-800">{subject}</span>
                    <div className={minimumLevelCellClassName}>
                      <span className="text-right text-sm font-medium text-gray-900">{group.min_level}</span>
                      <span className={`inline-block w-5 text-center ${isMatched ? 'text-green-600' : 'text-gray-300'}`}>{isMatched ? '✓' : ' '}</span>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}

          {/* Special programme display: one_of_electives (e.g. JS4238) */}
          {!hasElectiveAlternatives && customElectiveRequirements.length === 0 && result.one_of_electives && !result.one_of_groups && (
            <>
              {result.one_of_electives.subjects.length > 1 && (
                <div className="border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800">
                  {result.one_of_electives.requireCount === 2 ? 'Two of the following elective subjects' : 'One of the following elective subjects'}
                </div>
              )}
              {result.one_of_electives.subjects.map(subject => {
                const isMatched = result.one_of_electives?.matched_subjects?.includes(subject) || result.one_of_electives?.matched_subject === subject;
                return (
                  <div key={subject} className="flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50">
                    <span className="text-sm text-gray-800">{subject}</span>
                    <div className={minimumLevelCellClassName}>
                      <span className="text-right text-sm font-medium text-gray-900">{result.one_of_electives?.min_level ?? ''}</span>
                      <span className={`inline-block w-5 text-center ${isMatched ? 'text-green-600' : 'text-gray-300'}`}>{isMatched ? '✓' : ' '}</span>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* additional_any_one_subject - old position removed, now above */}

          {/* Primary source: elective_requirements - include_subjects rows as "One of" sections */}
          {!hasElectiveAlternatives && customElectiveRequirements.length > 0 && !hasSpecialPerDisplay && customElectiveRequirements
            .filter(r => isIncludeSubjectsRequirement(r))
            .map((requirement, index) => {
            const subjects = requirement.include_subjects!;
            const reqCount = requirement.requireCount || 1;
            return (
              <React.Fragment key={`er-inc-${index}`}>
                <div className="border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800">
                  {reqCount === 2 ? 'Two of the following elective subjects' : 'One of the following elective subjects'}
                </div>
                {subjects.map(subject => {
                  const isMatched = requirement.matched_subjects?.includes(subject);
                  return (
                    <div key={subject} className="flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50">
                      <span className="text-sm text-gray-800">{subject}</span>
                      <div className={minimumLevelCellClassName}>
                        <span className="text-right text-sm font-medium text-gray-900">{requirement.min_level ?? ''}</span>
                        <span className={`inline-block w-5 text-center ${isMatched ? 'text-green-600' : 'text-gray-300'}`}>{isMatched ? '✓' : ' '}</span>
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}

          {showGenericElectivesRow && result.electives && (
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50">
              <span className="text-sm text-gray-800">
                {formatAnySubjectLabel(result.electives.required, result.electives.include_cores)}
              </span>
              <div className={minimumLevelCellClassName}>
                <span className="text-right text-sm font-medium text-gray-900">
                  {result.electives.min_level ?? result.requirements?.['electives_min_level'] ?? ''}
                </span>
                <span className={`inline-block w-5 text-center ${result.electives.met ? 'text-green-600' : 'text-red-600'}`}>
                  {result.electives.met ? '✓' : '✗'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const GerBadge: React.FC<GerBadgeProps> = ({ ger, translate }) => {
  if (!ger) return null;

  const isEligible = ger.eligible;
  const {
    open,
    isMounted,
    wrapperRef,
    panelRef,
    panelStyle,
    handleTouchEnd,
    handleMouseEnter,
    handleMouseLeave,
    handlePanelMouseEnter,
    handlePanelMouseLeave,
  } = useTouchTooltip();

  return (
    <>
      <div
        ref={wrapperRef}
        className="relative inline-flex"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span
          role="button"
          tabIndex={0}
          className="inline-flex"
          style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          onTouchEnd={handleTouchEnd}
        >
          <Badge
            variant={isEligible ? 'lightBlue' : 'red'}
            className="cursor-pointer font-medium"
          >
            {translate('ger')} {isEligible ? '✓' : '✗'}
          </Badge>
        </span>
      </div>
      {open && isMounted && createPortal(
        <div
          ref={panelRef}
          style={panelStyle}
          className="animate-in fade-in zoom-in duration-200"
          onMouseEnter={handlePanelMouseEnter}
          onMouseLeave={handlePanelMouseLeave}
        >
          <RequirementsTable
            title="GENERAL ENTRANCE REQUIREMENTS"
            result={ger}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export const PerBadge: React.FC<PerBadgeProps> = ({ per, translate }) => {
  if (!per) return null;

  const isEligible = per.eligible;
  const {
    open,
    isMounted,
    wrapperRef,
    panelRef,
    panelStyle,
    handleTouchEnd,
    handleMouseEnter,
    handleMouseLeave,
    handlePanelMouseEnter,
    handlePanelMouseLeave,
  } = useTouchTooltip();

  return (
    <>
      <div
        ref={wrapperRef}
        className="relative inline-flex"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span
          role="button"
          tabIndex={0}
          className="inline-flex"
          style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          onTouchEnd={handleTouchEnd}
        >
          <Badge
            variant={isEligible ? 'purple' : 'red'}
            className="cursor-pointer font-medium"
          >
            {translate('per')} {isEligible ? '✓' : '✗'}
          </Badge>
        </span>
      </div>
      {open && isMounted && createPortal(
        <div
          ref={panelRef}
          style={panelStyle}
          className="animate-in fade-in zoom-in duration-200"
          onMouseEnter={handlePanelMouseEnter}
          onMouseLeave={handlePanelMouseLeave}
        >
          <RequirementsTable
            title="PROGRAMME ENTRANCE REQUIREMENTS"
            result={per}
          />
        </div>,
        document.body
      )}
    </>
  );
};
